"use client";

// ============================================================
// CLUBHUB – /pinnwand/aufgaben: Brett oder Liste
// ============================================================
// Dieselben Aufgaben in zwei Darstellungen (`?darstellung=liste`).
// Der Filterkopf ist der aller Listen: Suche, Faelligkeit (rot, was
// ueberfaellig ist — auch zugeklappt), in der Liste der Stand, und
// unten die Gesichter der Zustaendigen.
// BACKEND: Filtern auf den Server, gleiche Schluessel in der Adresse.
// ============================================================
import { useState } from "react";
import { CalendarDays, CircleCheck, Circle, Ellipsis, List, ListChecks, Pencil, Plus, SquareKanban, Trash2 } from "lucide-react";
import type { TaskStatus } from "@/generated/prisma/enums";
import {
  Avatar,
  Button,
  DialogAmRumpf,
  DropdownItem,
  DropdownLabel,
  DropdownMenu,
  DropdownSeparator,
  EmptyState,
  PageHeader,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  confirmDialog,
  toast,
} from "@/components/ui";
import {
  Filterkopf,
  Filterloesen,
  Filterzurueck,
  Fusstext,
  Listenblatt,
  Listenfuss,
  Listenkoerper,
  Listenseite,
  Listentabelle,
  Luecke,
  Personenreihe,
  Schiene,
  SortableHead,
  SortableHeaderRow,
  Suchfeld,
  Textzelle,
  UEBER_DER_ZEILE,
  useAnsicht,
  Waehler,
  Zaehlknopf,
  Zaehlschiene,
  ZEILENFLAECHE,
  type Person,
} from "@/components/liste";
import { TonBadge } from "@/components/mitglieder/status-badge";
import { TASK_STATUS } from "@/lib/labels";
import {
  AUFGABEN_DEFAULT_SORT,
  AUFGABEN_PARAMS,
  AUFGABEN_SORT,
  FAELLIGKEITEN,
  aufgabenFilterZahl,
  type AufgabenAnsicht,
  type Faelligkeit,
} from "@/lib/pinnwand/ansicht";
import { STATUS_FOLGE, faelligDieseWoche, faelligText, istUeberfaellig } from "@/lib/pinnwand/logik";
import { istFehler } from "@/lib/action-result";
import type { AufgabeView } from "@/lib/pinnwand/views";
import { sortRows } from "@/lib/sorting";
import { cn } from "@/lib/utils";
import { usePinnwand } from "./pinnwand-bestand";
import { PinnwandRegister } from "./pinnwand-teile";
import { AufgabenBrett } from "./aufgaben-brett";
import { AufgabeDialog } from "./aufgabe-dialog";

type Dimension = "faellig" | "status" | "person" | "frei";

export function Aufgaben({ ansicht }: { ansicht: AufgabenAnsicht }) {
  const p = usePinnwand();
  const { aufgaben, personen, heute, rechte } = p;
  const { setzen, isPending } = useAnsicht();
  const [dialog, setDialog] = useState<null | { aufgabe?: AufgabeView; status?: TaskStatus }>(null);
  const liste = ansicht.darstellung === "liste";

  const nadel = ansicht.q.toLowerCase();
  const passtFaellig = (a: AufgabeView, f: Faelligkeit) =>
    f === "ueberfaellig" ? istUeberfaellig(a, heute) : f === "woche" ? faelligDieseWoche(a, heute) : !a.dueDate && a.status !== "DONE";

  const passt = (a: AufgabeView, ausser?: Dimension) => {
    if (ausser !== "faellig" && ansicht.faellig && !passtFaellig(a, ansicht.faellig)) return false;
    if (liste && ausser !== "status" && ansicht.status && a.status !== ansicht.status) return false;
    if (ausser !== "person" && ansicht.personen.length > 0 && !ansicht.personen.includes(a.assigneeId ?? "")) return false;
    if (ausser !== "frei" && ansicht.offenFuerAlle && (a.assigneeId || a.status === "DONE")) return false;
    if (nadel) {
      const wer = a.assigneeId ? personen[a.assigneeId]?.displayName : "";
      if (![a.title, a.description, wer, a.eventTitle].filter(Boolean).join(" ").toLowerCase().includes(nadel)) return false;
    }
    return true;
  };

  const gezeigt = aufgaben.filter((a) => passt(a));
  const fuerFaellig = aufgaben.filter((a) => passt(a, "faellig"));
  const faelligZaehler = Object.fromEntries(FAELLIGKEITEN.map((f) => [f.schluessel, fuerFaellig.filter((a) => passtFaellig(a, f.schluessel)).length])) as Record<Faelligkeit, number>;
  const fuerStatus = aufgaben.filter((a) => passt(a, "status"));
  const fuerPerson = aufgaben.filter((a) => passt(a, "person"));
  const leute: Person[] = [
    ...fuerPerson
      .filter((a) => a.assigneeId && personen[a.assigneeId])
      .reduce((m, a) => {
        const pk = personen[a.assigneeId!]!;
        const da = m.get(pk.id);
        if (da) da.anzahl++;
        else m.set(pk.id, { id: pk.id, firstName: pk.firstName, lastName: pk.lastName, anzahl: 1 });
        return m;
      }, new Map<string, Person>())
      .values(),
  ].sort((x, y) => x.firstName.localeCompare(y.firstName, "de"));
  const freiZaehler = aufgaben.filter((a) => passt(a, "frei") && !a.assigneeId && a.status !== "DONE").length;

  const offen = aufgaben.filter((a) => a.status === "OPEN").length;
  const inArbeit = aufgaben.filter((a) => a.status === "IN_PROGRESS").length;
  const ueberfaellig = aufgaben.filter((a) => istUeberfaellig(a, heute)).length;
  const filterZahl = aufgabenFilterZahl(ansicht);
  const gezeigtUeber = gezeigt.filter((a) => istUeberfaellig(a, heute)).length;

  const sortiert = sortRows(
    gezeigt,
    ansicht.sort,
    {
      aufgabe: (a) => a.title,
      zustaendig: (a) => (a.assigneeId ? personen[a.assigneeId]?.displayName : null),
      faellig: (a) => a.dueDate,
      status: (a) => STATUS_FOLGE.indexOf(a.status),
    },
    (a, b) => STATUS_FOLGE.indexOf(a.status) - STATUS_FOLGE.indexOf(b.status) || a.title.localeCompare(b.title, "de"),
  );

  async function erledigen(a: AufgabeView) {
    const nach: TaskStatus = a.status === "DONE" ? "OPEN" : "DONE";
    const r = await p.aufgabeStatus(a.id, nach);
    if (istFehler(r)) {
      toast.error(r.error);
      return;
    }
    toast.success(nach === "DONE" ? `Erledigt: ${a.title}` : `Wieder offen: ${a.title}`, {
      action: { label: "Rückgängig", onClick: () => void standSetzen(a, a.status) },
    });
  }

  /** Stand aus dem Menue oder „Rückgängig“ — ein Fehler kommt als Meldung. */
  async function standSetzen(a: AufgabeView, s: TaskStatus) {
    const r = await p.aufgabeStatus(a.id, s);
    if (istFehler(r)) toast.error(r.error);
  }

  async function loeschen(a: AufgabeView) {
    const ja = await confirmDialog({
      title: `„${a.title}“ löschen?`,
      description: "Erledigtes besser auf „Erledigt“ setzen — dann bleibt sichtbar, wer es gemacht hat.",
      confirmLabel: "Löschen",
      danger: true,
    });
    if (!ja) return;
    const r = await p.aufgabeLoeschen(a.id);
    if (istFehler(r)) toast.error(r.error);
    else toast.success("Aufgabe gelöscht");
  }

  const zeilenMenue = (a: AufgabeView) => (
    <DropdownMenu
      trigger={
        <button type="button" aria-label={`Aktionen für „${a.title}“`} className="text-text-tertiary hover:bg-bg-tertiary hover:text-text-primary duration-fast grid h-8 w-8 place-items-center rounded-lg transition-colors">
          <Ellipsis className="h-4 w-4" aria-hidden />
        </button>
      }
    >
      <DropdownItem onClick={() => setDialog({ aufgabe: a })}>
        <Pencil className="h-4 w-4" aria-hidden />
        Öffnen
      </DropdownItem>
      {rechte.schreiben && (
        <>
          <DropdownSeparator />
          <DropdownLabel>Stand</DropdownLabel>
          {STATUS_FOLGE.map((s) => (
            <DropdownItem key={s} disabled={a.status === s} onClick={() => void standSetzen(a, s)}>
              <span className={cn("h-2 w-2 rounded-full", s === "DONE" ? "bg-success" : s === "IN_PROGRESS" ? "bg-accent-2" : "bg-text-tertiary")} aria-hidden />
              {TASK_STATUS[s].label}
            </DropdownItem>
          ))}
          <DropdownSeparator />
          <DropdownItem variant="danger" onClick={() => void loeschen(a)}>
            <Trash2 className="h-4 w-4" aria-hidden />
            Löschen
          </DropdownItem>
        </>
      )}
    </DropdownMenu>
  );

  const haken = (a: AufgabeView) =>
    rechte.schreiben ? (
      <button
        type="button"
        onClick={() => void erledigen(a)}
        title={a.status === "DONE" ? "Wieder öffnen" : "Als erledigt markieren"}
        aria-label={a.status === "DONE" ? `„${a.title}“ wieder öffnen` : `„${a.title}“ als erledigt markieren`}
        className={cn(UEBER_DER_ZEILE, "grid h-6 w-6 shrink-0 place-items-center rounded-full", a.status === "DONE" ? "text-success" : "text-text-tertiary hover:text-success")}
      >
        {a.status === "DONE" ? <CircleCheck className="h-4.5 w-4.5" aria-hidden /> : <Circle className="h-4.5 w-4.5" aria-hidden />}
      </button>
    ) : null;

  return (
    <Listenseite>
      <PageHeader
        title="Pinnwand"
        description={`${offen} offen · ${inArbeit} in Arbeit${ueberfaellig > 0 ? ` · ${ueberfaellig} überfällig` : ""}`}
        actions={
          <>
            <Schiene>
              <Waehler icon={SquareKanban} mitWinkel={false} aktiv={!liste} onClick={() => setzen({ darstellung: null, status: null })} aria-label="Als Brett">
                Brett
              </Waehler>
              <Waehler icon={List} mitWinkel={false} aktiv={liste} onClick={() => setzen({ darstellung: "liste" })} aria-label="Als Liste">
                Liste
              </Waehler>
            </Schiene>
            {rechte.schreiben && (
              <Button onClick={() => setDialog({})}>
                <Plus className="h-4 w-4" aria-hidden />
                Neue Aufgabe
              </Button>
            )}
          </>
        }
      />
      <PinnwandRegister />

      <Listenblatt>
        <Filterkopf
          isPending={isPending}
          oben={
            <>
              <Suchfeld wert={ansicht.q} platzhalter="Aufgabe, Person, Termin …" beschriftung="Aufgaben durchsuchen" />
              <Luecke />
              <Zaehlschiene titel="Fällig">
                {FAELLIGKEITEN.map((f) => (
                  <Zaehlknopf
                    key={f.schluessel}
                    label={f.label}
                    anzahl={faelligZaehler[f.schluessel]}
                    aktiv={ansicht.faellig === f.schluessel}
                    ton={f.schluessel === "ueberfaellig" ? "gefahr" : "normal"}
                    titel={f.hinweis}
                    onClick={() => setzen({ faellig: ansicht.faellig === f.schluessel ? null : f.schluessel })}
                  />
                ))}
              </Zaehlschiene>
              {liste && (
                <Zaehlschiene titel="Stand">
                  {STATUS_FOLGE.map((s) => (
                    <Zaehlknopf
                      key={s}
                      label={TASK_STATUS[s].label}
                      anzahl={fuerStatus.filter((a) => a.status === s).length}
                      aktiv={ansicht.status === s}
                      onClick={() => setzen({ status: ansicht.status === s ? null : s.toLowerCase() })}
                    />
                  ))}
                </Zaehlschiene>
              )}
            </>
          }
          unten={
            <>
              <Personenreihe
                label="Zuständig"
                hinweis="Wer sich um die Aufgabe kümmert"
                personen={leute}
                gewaehlt={ansicht.personen}
                einheit={["Aufgabe", "Aufgaben"]}
                onWaehlen={(id) => {
                  const next = ansicht.personen.includes(id) ? ansicht.personen.filter((x) => x !== id) : [...ansicht.personen, id];
                  setzen({ person: next.length ? next.join(",") : null, frei: null });
                }}
              />
              <Schiene>
                <Zaehlknopf
                  label="Niemand zuständig"
                  anzahl={freiZaehler}
                  aktiv={ansicht.offenFuerAlle}
                  titel="Offene Aufgaben, die noch jemand übernehmen kann"
                  onClick={() => setzen({ frei: ansicht.offenFuerAlle ? null : "1", person: null })}
                />
              </Schiene>
              <Filterzurueck anzahl={filterZahl} params={AUFGABEN_PARAMS} />
            </>
          }
        />

        {gezeigt.length === 0 && (filterZahl > 0 || aufgaben.length === 0) ? (
          <Listenkoerper className="p-5">
            {filterZahl > 0 ? (
              <EmptyState icon={ListChecks} title="Keine Aufgabe passt zu diesem Filter" description="Ändere die Suche oder setze die Filter zurück." action={<Filterloesen params={AUFGABEN_PARAMS} />} />
            ) : (
              <EmptyState
                icon={ListChecks}
                title="Keine Aufgaben"
                description="Was im Verein zu tun ist — Hallenzeiten, Getränke, Trikots —, mit Zuständigen und Fälligkeit."
                action={
                  rechte.schreiben ? (
                    <Button onClick={() => setDialog({})}>
                      <Plus className="h-4 w-4" aria-hidden />
                      Neue Aufgabe
                    </Button>
                  ) : undefined
                }
              />
            )}
          </Listenkoerper>
        ) : !liste ? (
          <Listenkoerper className="p-4">
            <AufgabenBrett aufgaben={gezeigt} onOeffnen={(a) => setDialog({ aufgabe: a })} onNeu={(status) => setDialog({ status })} />
          </Listenkoerper>
        ) : (
          <>
            <Listentabelle viewportClassName="max-md:hidden">
              <TableHeader>
                <SortableHeaderRow sort={ansicht.sort} defaultColumn={AUFGABEN_DEFAULT_SORT}>
                  <TableHead className="w-8" aria-label="Erledigt" />
                  <SortableHead column="aufgabe" {...AUFGABEN_SORT.aufgabe}>AUFGABE</SortableHead>
                  <SortableHead column="zustaendig" {...AUFGABEN_SORT.zustaendig}>ZUSTÄNDIG</SortableHead>
                  <SortableHead column="faellig" {...AUFGABEN_SORT.faellig}>FÄLLIG</SortableHead>
                  <SortableHead column="status" {...AUFGABEN_SORT.status}>STAND</SortableHead>
                  <TableHead className="w-10" aria-label="Aktionen" />
                </SortableHeaderRow>
              </TableHeader>
              <TableBody>
                {sortiert.map((a) => {
                  const pk = a.assigneeId ? personen[a.assigneeId] : undefined;
                  const ueber = istUeberfaellig(a, heute);
                  return (
                    <TableRow key={a.id} className="border-border-subtle border-b">
                      <TableCell className="w-8 pr-0">{haken(a)}</TableCell>
                      <Textzelle
                        text={
                          <button type="button" onClick={() => setDialog({ aufgabe: a })} className={cn(ZEILENFLAECHE, "hover:text-accent text-left font-medium transition-colors", a.status === "DONE" && "text-text-secondary line-through decoration-1")}>
                            {a.title}
                          </button>
                        }
                        zusatz={
                          a.eventTitle ? (
                            <span className="text-caption text-text-tertiary inline-flex items-center gap-1">
                              <CalendarDays className="h-3 w-3" aria-hidden />
                              {a.eventTitle}
                            </span>
                          ) : undefined
                        }
                      />
                      <TableCell className="whitespace-nowrap">
                        {pk ? (
                          <span className="flex items-center gap-2">
                            <Avatar firstName={pk.firstName} lastName={pk.lastName} size="sm" className="h-6 w-6 text-[10px]" inRow hint={false} />
                            {pk.displayName}
                          </span>
                        ) : (
                          <span className="text-text-tertiary">Niemand</span>
                        )}
                      </TableCell>
                      <TableCell className={cn("numeric whitespace-nowrap", ueber ? "text-danger font-medium" : a.dueDate ? "text-text-secondary" : "text-text-tertiary")}>
                        {faelligText(a, heute) ?? "–"}
                      </TableCell>
                      <TableCell>
                        <TonBadge ton={TASK_STATUS[a.status].ton}>{TASK_STATUS[a.status].label}</TonBadge>
                      </TableCell>
                      <TableCell className="w-10 pr-2">
                        <div className={UEBER_DER_ZEILE}>{zeilenMenue(a)}</div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Listentabelle>

            <Listenkoerper className="p-3 md:hidden">
              <ul className="bg-bg-primary border-border divide-border-subtle divide-y overflow-hidden rounded-xl border">
                {sortiert.map((a) => {
                  const pk = a.assigneeId ? personen[a.assigneeId] : undefined;
                  const ueber = istUeberfaellig(a, heute);
                  return (
                    <li key={a.id} className={cn("relative flex items-start gap-3 px-3 py-3", ueber && "shadow-[inset_3px_0_0_var(--color-danger)]")}>
                      {haken(a)}
                      <div className="min-w-0 flex-1">
                        <button type="button" onClick={() => setDialog({ aufgabe: a })} className={cn(ZEILENFLAECHE, "text-body-sm block text-left font-medium", a.status === "DONE" ? "text-text-secondary line-through decoration-1" : "text-text-primary")}>
                          {a.title}
                        </button>
                        <p className="text-caption mt-0.5 flex flex-wrap gap-x-2">
                          {faelligText(a, heute) && <span className={cn("numeric", ueber ? "text-danger font-medium" : "text-text-tertiary")}>{faelligText(a, heute)}</span>}
                          <span className="text-text-tertiary">{pk ? pk.displayName : "Niemand zuständig"}</span>
                        </p>
                      </div>
                      <TonBadge ton={TASK_STATUS[a.status].ton} className="mt-0.5">
                        {TASK_STATUS[a.status].label}
                      </TonBadge>
                    </li>
                  );
                })}
              </ul>
            </Listenkoerper>
          </>
        )}

        <Listenfuss>
          <Fusstext titel="Summe" zusatz={`${gezeigt.length} ${gezeigt.length === 1 ? "Aufgabe" : "Aufgaben"}`} />
          {gezeigtUeber > 0 && (
            <span className="text-body-sm text-text-secondary">
              Überfällig <span className="numeric text-danger font-medium">{gezeigtUeber}</span>
            </span>
          )}
          <span className="text-body-sm text-text-secondary">
            Erledigt <span className="numeric text-text-primary font-medium">{gezeigt.filter((a) => a.status === "DONE").length}</span>
          </span>
        </Listenfuss>
      </Listenblatt>

      <DialogAmRumpf>
        <AufgabeDialog offen={dialog !== null} onClose={() => setDialog(null)} aufgabe={dialog?.aufgabe} vorgabeStatus={dialog?.status} />
      </DialogAmRumpf>
    </Listenseite>
  );
}
